import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/apiConfig';
import { UserCircleIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import PersonForm from './forms/PersonForm';
import { Button, Alert, Spinner } from './ui';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

// Interface for Person data - should match definition in PeoplePage or a shared types file
interface Person {
  id: number;
  name: string;
  relationship?: string;
  notes?: string;
  appreciation?: string;
  triggers?: string;
  last_interaction?: string;
  is_self?: boolean;
}

const PeopleList: React.FC = () => {
  const navigate = useNavigate();
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchPeople = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/people/');
      setPeople(response.data);
    } catch (error) {
      console.error('Error fetching people:', error);
      setError('Failed to load people');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPeople();
  }, []);

  const handleEdit = (person: Person) => {
    setSelectedPerson(person);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (personId: number) => {
    if (!window.confirm('Are you sure you want to delete this person?')) {
      return;
    }

    try {
      const response = await apiClient.delete(`/api/people/${personId}/`);

      if (response.status >= 200 && response.status < 300) {
        setPeople(people.filter(person => person.id !== personId));
        setDeleteError(null);
      } else {
        const errorData = response.data;
        throw new Error(errorData?.detail || `Failed to delete person (${response.status})`);
      }
    } catch (error) {
      console.error('Error deleting person:', error);
      setDeleteError("Failed to delete person. Please try again.");
      setTimeout(() => setDeleteError(null), 3000);
    }
  };

  const handlePersonUpdated = () => {
    fetchPeople();
    setIsEditModalOpen(false);
    setSelectedPerson(null);
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return null;
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch {
      return "Invalid Date";
    }
  };

  // Sort people so is_self comes first
  const sortedPeople = [...people].sort((a, b) => (b.is_self ? 1 : 0) - (a.is_self ? 1 : 0));

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: SPACING[24]
      }}>
        <Spinner size="md" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      {error && (
        <Alert variant="error">{error}</Alert>
      )}

      {deleteError && (
        <Alert variant="error">{deleteError}</Alert>
      )}

      {people.length === 0 && !error && !loading && (
        <p style={{
          textAlign: 'center',
          color: COLORS.text.secondary,
          paddingTop: SPACING[16],
          paddingBottom: SPACING[16],
          fontSize: TYPOGRAPHY.size.base
        }}>No people found.</p>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(1, 1fr)',
        gap: SPACING[16]
      }}>
        {sortedPeople.map((person) => (
          <div
            key={person.id}
            style={{
              backgroundColor: person.is_self ? '#EFF6FF' : COLORS.bg.panel,
              border: `1px solid ${person.is_self ? '#93C5FD' : COLORS.border.default}`,
              borderRadius: RADIUS,
              padding: SPACING[16],
              cursor: 'pointer',
              transition: 'box-shadow 150ms ease'
            }}
            onClick={() => navigate(`/people/${person.id}`)}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: SPACING[12]
              }}>
                <div style={{
                  padding: SPACING[8],
                  backgroundColor: '#EFF6FF',
                  borderRadius: '50%'
                }}>
                  <UserCircleIcon style={{ width: '20px', height: '20px', color: COLORS.accent.default }} />
                </div>
                <div>
                  <h3 style={{
                    fontSize: TYPOGRAPHY.size.base,
                    fontWeight: TYPOGRAPHY.weight.semibold,
                    color: COLORS.text.primary
                  }}>{person.name}</h3>
                  {person.is_self && (
                    <span style={{
                      display: 'inline-block',
                      fontSize: TYPOGRAPHY.size.xs,
                      fontWeight: TYPOGRAPHY.weight.semibold,
                      color: '#1D4ED8',
                      backgroundColor: '#DBEAFE',
                      borderRadius: RADIUS,
                      paddingLeft: SPACING[8],
                      paddingRight: SPACING[8],
                      paddingTop: '2px',
                      paddingBottom: '2px',
                      marginLeft: SPACING[4],
                      verticalAlign: 'middle'
                    }}>Myself</span>
                  )}
                  {person.relationship && (
                    <p style={{
                      marginTop: SPACING[4],
                      fontSize: TYPOGRAPHY.size.sm,
                      color: COLORS.text.secondary
                    }}>{person.relationship}</p>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: SPACING[8] }} onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(person)}
                  title="Edit person"
                >
                  <PencilIcon style={{ width: '16px', height: '16px' }} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(person.id)}
                  title="Delete person"
                >
                  <TrashIcon style={{ width: '16px', height: '16px' }} />
                </Button>
              </div>
            </div>

            {person.appreciation && (
              <div style={{ marginTop: SPACING[12] }}>
                <h4 style={{
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary
                }}>Appreciation</h4>
                <p style={{
                  marginTop: SPACING[4],
                  fontSize: TYPOGRAPHY.size.sm,
                  color: COLORS.text.secondary,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}>{person.appreciation}</p>
              </div>
            )}

            {person.triggers && (
              <div style={{ marginTop: SPACING[12] }}>
                <h4 style={{
                  fontSize: TYPOGRAPHY.size.xs,
                  fontWeight: TYPOGRAPHY.weight.medium,
                  color: COLORS.text.secondary
                }}>Triggers</h4>
                <p style={{
                  marginTop: SPACING[4],
                  fontSize: TYPOGRAPHY.size.sm,
                  color: COLORS.text.secondary,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}>{person.triggers}</p>
              </div>
            )}

            {person.last_interaction && (
              <p style={{
                fontSize: TYPOGRAPHY.size.xs,
                color: COLORS.text.tertiary,
                marginTop: SPACING[12]
              }}>
                Last interaction: {formatDate(person.last_interaction)}
              </p>
            )}
          </div>
        ))}
      </div>

      {selectedPerson && (
        <PersonForm
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedPerson(null);
          }}
          onPersonCreated={handlePersonUpdated}
          initialPerson={selectedPerson}
          isEditMode={true}
        />
      )}
    </div>
  );
};

export default PeopleList;
